import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { CartProvider } from "@/context/CartContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import CustomerProtectedRoute from "@/components/public/CustomerProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";

// Public
import Home from "@/pages/public/Home";
import Watches from "@/pages/public/Watches";
import WatchDetail from "@/pages/public/WatchDetail";
import VipJoin from "@/pages/public/VipJoin";
import RequestWatch from "@/pages/public/RequestWatch";
import Bespoke from "@/pages/public/Bespoke";
import SellConsign from "@/pages/public/SellConsign";
import Merch from "@/pages/public/Merch";
import MerchDetail from "@/pages/public/MerchDetail";
import Parts from "@/pages/public/Parts";
import PartDetail from "@/pages/public/PartDetail";
import Checkout from "@/pages/public/Checkout";
import About from "@/pages/public/About";
import Journal from "@/pages/public/Journal";
import JournalArticle from "@/pages/public/JournalArticle";
import CustomerLogin from "@/pages/public/CustomerLogin";
import CustomerRegister from "@/pages/public/CustomerRegister";
import CustomerAccount from "@/pages/public/CustomerAccount";
import GoogleAuthCallback from "@/pages/public/GoogleAuthCallback";
import VeteransCircle from "@/pages/public/VeteransCircle";
import LegalStub from "@/pages/public/LegalStub";
import VeteranProtectedRoute from "@/components/public/VeteranProtectedRoute";

// Admin
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminWatches from "@/pages/admin/AdminWatches";
import AdminWatchForm from "@/pages/admin/AdminWatchForm";
import AdminVipLeads from "@/pages/admin/AdminVipLeads";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminSellers from "@/pages/admin/AdminSellers";
import AdminTasks from "@/pages/admin/AdminTasks";
import AdminDossiers from "@/pages/admin/AdminDossiers";
import AdminMerch from "@/pages/admin/AdminMerch";
import AdminParts from "@/pages/admin/AdminParts";
import AdminPartForm from "@/pages/admin/AdminPartForm";
import AdminContent from "@/pages/admin/AdminContent";
import AdminSitePages from "@/pages/admin/AdminSitePages";
import AdminJournal from "@/pages/admin/AdminJournal";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminOrders from "@/pages/admin/AdminOrders";

function AppRoutes() {
  return (
    <Routes>
      {/* Public site */}
      <Route path="/" element={<Home />} />
      <Route path="/watches" element={<Watches />} />
      <Route path="/watches/:id" element={<WatchDetail />} />
      <Route path="/vip" element={<VipJoin />} />
      <Route path="/request" element={<RequestWatch />} />
      <Route path="/bespoke" element={<Bespoke />} />
      <Route path="/sell" element={<SellConsign />} />
      <Route path="/shop" element={<Merch />} />
      <Route path="/shop/:slug" element={<MerchDetail />} />
      <Route path="/parts" element={<Parts />} />
      <Route path="/parts/:id" element={<PartDetail />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/about" element={<About />} />
      <Route path="/journal" element={<Journal />} />
      <Route path="/journal/:slug" element={<JournalArticle />} />
      <Route path="/account/login" element={<CustomerLogin />} />
      <Route path="/account/register" element={<CustomerRegister />} />
      <Route
        path="/veterans"
        element={
          <VeteranProtectedRoute>
            <VeteransCircle />
          </VeteranProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <CustomerProtectedRoute>
            <CustomerAccount />
          </CustomerProtectedRoute>
        }
      />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="watches" element={<AdminWatches />} />
        <Route path="watches/new" element={<AdminWatchForm />} />
        <Route path="watches/:id" element={<AdminWatchForm />} />
        <Route path="leads" element={<AdminVipLeads />} />
        <Route path="requests" element={<AdminRequests />} />
        <Route path="sellers" element={<AdminSellers />} />
        <Route path="dossiers" element={<AdminDossiers />} />
        <Route path="merch" element={<AdminMerch />} />
        <Route path="parts" element={<AdminParts />} />
        <Route path="parts/new" element={<AdminPartForm />} />
        <Route path="parts/:id" element={<AdminPartForm />} />
        <Route path="cms" element={<AdminSitePages />} />
        <Route path="about" element={<AdminContent />} />
        <Route path="journal" element={<AdminJournal />} />
        <Route path="tasks" element={<AdminTasks />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>

                    <Route path="/privacy" element={<LegalStub kind="privacy" />} />
              <Route path="/terms" element={<LegalStub kind="terms" />} />
              <Route path="/returns" element={<LegalStub kind="returns" />} />
              <Route path="/shipping" element={<LegalStub kind="shipping" />} />
              <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Intercepts the Emergent OAuth return (URL fragment carries session_id) BEFORE
// rendering normal routes to avoid race conditions with auth checks.
function RootView() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }
  return <AppRoutes />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AdminAuthProvider>
            <CustomerAuthProvider>
              <CartProvider>
                <Toaster position="top-right" richColors />
                <RootView />
              </CartProvider>
            </CustomerAuthProvider>
          </AdminAuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
